/**
 * Tiny time-series chart for the Usage tab. Inline SVG, no library — bars
 * for authenticated request counts per bucket. Hover a bar for the count
 * plus the distinct users in that bucket.
 *
 * The chart auto-sizes to its container width via `width="100%"` +
 * viewBox; we render a fixed coordinate system (1000×height) and let the
 * browser scale it down.
 *
 * One series only, by design. We used to overlay a unique-users line on
 * the same axes, but that put two different scales on one chart with no
 * legend or axis label — readers couldn't tell what the line meant or
 * how it related to the bars. The unique-user count is now surfaced
 * through the per-bucket tooltip, where it's unambiguous.
 *
 * The backend pads the series with zero buckets for empty days/hours
 * (see `fillSeriesGaps` in services/usage.ts) so the time axis is
 * honest — a quiet day shows up as a flat bar, not a missing gap.
 */

import { useMemo } from 'react';
import type { UsageSeriesPoint, UsageRange } from '../api/types.js';

interface UsageChartProps {
  series: UsageSeriesPoint[];
  range: UsageRange;
  height?: number;
}

const VIEW_W = 1000;
const PADDING_X = 12;
const PADDING_TOP = 8;
const PADDING_BOTTOM = 22;

export function UsageChart({ series, range, height = 180 }: UsageChartProps) {
  const { bars, maxEvents, xLabels } = useMemo(
    () => prepare(series, range, height),
    [series, range, height],
  );

  const totalEvents = series.reduce((sum, p) => sum + p.events, 0);
  if (series.length === 0 || totalEvents === 0) {
    return (
      <div
        className="flex items-center justify-center text-faint text-sm rounded-lg border border-dashed border-line"
        style={{ height }}
      >
        No activity in this range yet.
      </div>
    );
  }

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${VIEW_W} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Authenticated requests over the last ${range}`}
    >
      {/* Subtle baseline */}
      <line
        x1={PADDING_X}
        x2={VIEW_W - PADDING_X}
        y1={height - PADDING_BOTTOM}
        y2={height - PADDING_BOTTOM}
        stroke="rgb(var(--line))"
        strokeWidth={1}
      />
      {/* Bars — request count per bucket */}
      {bars.map((b, i) => (
        <rect
          key={i}
          x={b.x}
          y={b.y}
          width={b.w}
          height={b.h}
          rx={2}
          fill="rgb(var(--brand) / 0.65)"
        >
          <title>
            {b.label}: {b.events} request{b.events === 1 ? '' : 's'}
            {b.users > 0 ? ` from ${b.users} user${b.users === 1 ? '' : 's'}` : ''}
          </title>
        </rect>
      ))}
      {/* X-axis labels */}
      {xLabels.map((lab, i) => (
        <text
          key={i}
          x={lab.x}
          y={height - 6}
          fontSize={11}
          fill="rgb(var(--faint))"
          textAnchor="middle"
        >
          {lab.text}
        </text>
      ))}
      {/* Top-right peak label — explicit so "peak NN" isn't ambiguous */}
      <text
        x={VIEW_W - PADDING_X}
        y={PADDING_TOP + 10}
        fontSize={11}
        fill="rgb(var(--faint))"
        textAnchor="end"
      >
        peak {maxEvents} req{maxEvents === 1 ? '' : 's'}/{range === 'day' ? 'hr' : 'day'}
      </text>
    </svg>
  );
}

interface PreparedBar {
  x: number;
  y: number;
  w: number;
  h: number;
  events: number;
  users: number;
  label: string;
}
interface PreparedXLabel {
  x: number;
  text: string;
}

function prepare(
  series: UsageSeriesPoint[],
  range: UsageRange,
  height: number,
): {
  bars: PreparedBar[];
  maxEvents: number;
  xLabels: PreparedXLabel[];
} {
  const n = series.length;
  const plotW = VIEW_W - PADDING_X * 2;
  const plotH = height - PADDING_TOP - PADDING_BOTTOM;
  const maxEvents = Math.max(1, ...series.map((p) => p.events));

  const slotW = plotW / Math.max(n, 1);
  const barW = Math.max(2, slotW * 0.72);

  const bars: PreparedBar[] = series.map((p, i) => {
    const slotX = PADDING_X + i * slotW;
    const cx = slotX + slotW / 2;
    const h = (p.events / maxEvents) * plotH;
    return {
      x: cx - barW / 2,
      y: PADDING_TOP + (plotH - h),
      w: barW,
      h,
      events: p.events,
      users: p.uniqueUsers,
      label: formatBucketLabel(p.t, range),
    };
  });

  // Sparse x-axis: first, last, and ~3 ticks in between, evenly spaced.
  const tickCount = Math.min(5, n);
  const xLabels: PreparedXLabel[] = [];
  if (tickCount >= 1) {
    for (let i = 0; i < tickCount; i++) {
      const idx = Math.round((i / (tickCount - 1 || 1)) * (n - 1));
      const p = series[idx]!;
      const slotX = PADDING_X + idx * slotW + slotW / 2;
      xLabels.push({ x: slotX, text: formatXLabel(p.t, range) });
    }
  }
  return { bars, maxEvents, xLabels };
}

function formatBucketLabel(epochSec: number, range: UsageRange): string {
  const d = new Date(epochSec * 1000);
  if (range === 'day') {
    return d.toLocaleString(undefined, { hour: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatXLabel(epochSec: number, range: UsageRange): string {
  const d = new Date(epochSec * 1000);
  if (range === 'day') return d.toLocaleString(undefined, { hour: 'numeric' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// --- Per-user sparkline (used in the user table) -------------------------

interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
}

/**
 * Minimal area sparkline. No axes, no labels — visual cue only. Renders
 * as a fixed-aspect SVG; caller sets width/height for the layout.
 */
export function Sparkline({ values, width = 80, height = 20 }: SparklineProps) {
  if (values.length === 0) {
    return <span className="text-faint text-xs">—</span>;
  }
  const max = Math.max(1, ...values);
  const stepX = width / Math.max(values.length - 1, 1);
  const points = values
    .map((v, i) => `${(i * stepX).toFixed(2)},${(height - (v / max) * height).toFixed(2)}`)
    .join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <polyline
        points={points}
        fill="none"
        stroke="rgb(var(--brand))"
        strokeWidth={1.25}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
